import { useGlobalState } from 'context/GlobalStateContext'
import { useUserSelectUsersQuery } from 'schema'
import { DefaultQueryHookOptions } from 'utils/defaults'
import { useOptions } from '../../hooks/useOptions'
import { UserSelectContext } from './types'
import { useUserSelectTooltip } from './hooks/useUserSelectTooltip'
import { useUserSelectLabel } from './hooks/useUserSelectLabel'
import { useUserDeleted } from '../../../../hooks/useUserDeleted'
import { createStrictContext } from 'utils/createContext'
import { toLabeledValues } from '../../utils/labeledValues'
import { alphabeticalCompare } from 'shared'

const { Provider, useContextFn } =
  createStrictContext<UserSelectContext>('UserSelectContext')

/**
 * returns the context associated with the {@link UserSelectProvider}
 *
 * @throws if the UserSelectProvider is not found
 */
export const useUserSelectContext = useContextFn

/**
 * a provider to give access to data and methods associated with the UserSelect
 *
 * @param props
 */
const UserSelectProvider: React.FC = ({ children }) => {
  const {
    state: { projectGlobalId: globalId },
  } = useGlobalState()
  const { data, loading } = useUserSelectUsersQuery({
    ...DefaultQueryHookOptions,
    skip: !globalId,
    variables: { globalId: globalId! },
  })
  const label = useUserSelectLabel()
  const tooltip = useUserSelectTooltip()
  const deleted = useUserDeleted()
  const options = useOptions({
    items: data?.project?.users,
    value: 'id',
    label,
    tooltip,
    sort: (a, b) => alphabeticalCompare(label(a), label(b)),
  })
  return (
    <Provider
      value={{
        loading,
        options,
        fromLabeledValues: (values) =>
          (values ?? []).map(({ value }) => value as string),
        fromLabeledValue: (value) => value?.value as string,
        toLabeledValues: (records) =>
          toLabeledValues(records, { value: 'id', label, tooltip, deleted }),
        toLabeledValue: (record) =>
          record
            ? toLabeledValues([record], {
                value: 'id',
                label,
                tooltip,
                deleted,
              })[0]
            : undefined,
      }}
    >
      {children}
    </Provider>
  )
}

export default UserSelectProvider
